/**
 * Placement validation for visibility rules in the builder: returns the
 * entries `[{ index, item, ruleIndexes }]` of rules that break the picker's
 * placement model — a question's trigger shares the dependent's section-unit
 * and comes before it; a section's trigger is root-zone only. Dangling, self
 * and type rules are flagged per rule; chains are accepted (backend
 * authority). Rules beyond the three-per-item maximum are flagged. These are
 * placement errors only — `checklistIsValid` never reacts to them.
 */
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import {
  ITEM_TYPES,
  MAX_VISIBILITY_RULES
} from '@/components/Checklists/ChecklistCreation/constants'

import {
  findSectionStart,
  isAllowedTriggerItem
} from './getEligibleTriggerQuestions'

function getRuleIndexesWithError(items, indexByItemId, item, index) {
  const ruleIndexes = []

  item.visibilityRules.forEach((rule, ruleIndex) => {
    if (ruleIndex >= MAX_VISIBILITY_RULES) {
      ruleIndexes.push(ruleIndex)
      return
    }

    const triggerIndex = indexByItemId.get(
      utils.getEntityId(rule?.triggerItemId)
    )

    if (_.isUndefined(triggerIndex)) {
      ruleIndexes.push(ruleIndex)
      return
    }

    if (triggerIndex === index) {
      ruleIndexes.push(ruleIndex)
      return
    }

    if (!isAllowedTriggerItem(items[triggerIndex])) {
      ruleIndexes.push(ruleIndex)
      return
    }

    if (triggerIndex > index) {
      ruleIndexes.push(ruleIndex)
      return
    }

    // Section triggers: root zone only.
    if (
      item.type === ITEM_TYPES.SEPARATOR &&
      findSectionStart({ items, itemIndex: triggerIndex }) !== null
    ) {
      ruleIndexes.push(ruleIndex)
      return
    }

    if (item.type === ITEM_TYPES.SEPARATOR) {
      return
    }

    if (
      findSectionStart({ items, itemIndex: triggerIndex }) !==
      findSectionStart({ items, itemIndex: index })
    ) {
      ruleIndexes.push(ruleIndex)
    }
  })

  return ruleIndexes
}

export default function getVisibilityRuleErrors(items) {
  if (!_.isArray(items)) {
    return []
  }

  const indexByItemId = new Map()
  items.forEach((item, index) => {
    const itemId = utils.getEntityId(item?._id)
    if (itemId) {
      indexByItemId.set(itemId, index)
    }
  })

  const itemsWithError = []
  items.forEach((item, index) => {
    if (!utils.checklists.hasVisibilityRule(item)) {
      return
    }

    const ruleIndexes = getRuleIndexesWithError(
      items,
      indexByItemId,
      item,
      index
    )

    if (_.isEmpty(ruleIndexes)) {
      return
    }

    itemsWithError.push({ index, item, ruleIndexes })
  })

  return itemsWithError
}
